import * as React from "react"

import { cn } from "@/lib/utils"
import { shouldSuppressAutoFocus } from "@/hooks/use-mobile"

const Input = React.forwardRef<HTMLInputElement, React.ComponentProps<"input">>(
  ({ className, type, onFocus, onMouseUp, autoFocus, ...props }, ref) => {
    // TECH-011 — Sur mobile/tactile, on neutralise `autoFocus` : un focus
    // automatique ferait surgir le clavier virtuel à l'ouverture d'une modale,
    // masquant le contenu. Évalué seulement si autoFocus est demandé (court-
    // circuit → aucun coût pour la grande majorité des champs sans autoFocus).
    const resolvedAutoFocus =
      autoFocus && !shouldSuppressAutoFocus() ? true : undefined
    // CLI-009 Addendum §10 (2026-06-04) — Pour les champs numériques, sélectionner
    // toute la valeur au focus : sinon, sur un champ déjà rempli (ex. « 0 »),
    // taper un chiffre l'ajoute (« 05 ») au lieu de le remplacer.
    //
    // Mécanique fiable (le `requestAnimationFrame` était racé par le mouseup) :
    //   1. au focus, on sélectionne tout ET on arme un drapeau ;
    //   2. au PREMIER mouseup (celui du clic qui a donné le focus), on
    //      `preventDefault` pour empêcher le navigateur de recoller le caret et
    //      d'annuler la sélection. Les clics suivants (champ déjà focus) ne sont
    //      pas interceptés → l'utilisateur peut toujours positionner son curseur.
    const selectOnNextMouseUp = React.useRef(false)
    const handleFocus = React.useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        if (type === "number") {
          selectOnNextMouseUp.current = true
          try {
            e.currentTarget.select()
          } catch {
            /* noop */
          }
        }
        onFocus?.(e)
      },
      [type, onFocus],
    )
    const handleMouseUp = React.useCallback(
      (e: React.MouseEvent<HTMLInputElement>) => {
        if (type === "number" && selectOnNextMouseUp.current) {
          selectOnNextMouseUp.current = false
          e.preventDefault() // conserve la sélection posée au focus
        }
        onMouseUp?.(e)
      },
      [type, onMouseUp],
    )
    return (
      <input
        type={type}
        onFocus={handleFocus}
        onMouseUp={handleMouseUp}
        className={cn(
          "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground placeholder:italic placeholder:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          className
        )}
        ref={ref}
        autoFocus={resolvedAutoFocus}
        {...props}
      />
    )
  }
)
Input.displayName = "Input"

export { Input }
